<template>
  <div class="box-dark">
    <div class="account-header">
      <div class="account-header-title">
        <img v-if="account.logo_url" :src="account.logo_url" :alt="account.name" class="bank-icon" />
        <ImageContainer v-else :icon="null" :color="null" :source="account.id_bank + '.png'" />
        <div class="bank-details">
          <h1>
            <template v-if="account.id_bank_data">
              {{ account.id_bank_data.name }}
              <br />
            </template>
            {{ account.name }}
          </h1>
          <p>
            {{ account.label }}
          </p>
        </div>
      </div>
      <div style="flex-shrink: 0; margin-left: 0.75rem">
        <i class="fa-solid fa-pen" style="margin-right: 1rem" @click="openModal(account)" />
        <i
          :class="isDeleting ? 'fas fa-spinner fa-spin' : 'fa-solid fa-trash delete-icon'"
          :style="isDeleting ? 'opacity: 0.5; cursor: not-allowed' : ''"
          @click="!isDeleting && confirmDelete(account.id)"
        />
      </div>
    </div>
    <div class="row mt-3 mx-1">
      <button class="btn-default-transparent no-width col-auto" @click="emitAccDetail">
        <i class="fa-solid fa-eye" />
        <span> Ver detalhes</span>
      </button>
    </div>
    <div class="separator" />
    <div class="balance" v-if="account.balance != null">
      <p>Saldo</p>
      <p :class="hideCurrency ? 'blur-effect' : ''">
        {{ formatValue(account.balance != null ? account.balance : 0) }}
      </p>
    </div>

    <!-- Open Finance sync status row -->
    <div v-if="account.open_finance_account_id" class="sync-row">
      <span class="sync-label">
        Última atualização:
        <strong>{{ formattedLastSync }}</strong>
      </span>
      <button
        class="btn-sync"
        :disabled="isSyncing || cooldownRemaining > 0"
        :title="syncTooltip"
        @click="requestSync"
      >
        <i :class="isSyncing ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" />
        {{ syncButtonLabel }}
      </button>
    </div>
  </div>
  <Toast />
</template>

<script>
import { useAppStore } from "@/store/store.js";
import { useOpenFinanceStore } from "@/store/open_finance/store.js";
import { mapState } from "pinia";
import ImageContainer from "@/components/shared/ImageContainer.vue";
import axios from "axios";
import { useConfirm } from "primevue/useconfirm";

export default {
  components: { ImageContainer },
  props: {
    account: {},
    color: String,
  },
  emits: ["accDetail", "editAccount"],

  data() {
    return {
      isSyncing: false,
      isDeleting: false,
      cooldownRemaining: 0,
      cooldownTimer: null,
    };
  },

  computed: {
    ...mapState(useAppStore, ["hideCurrency"]),

    isCardAccount() {
      return this.account.id_account_type == "4";
    },

    token() {
      return useAppStore().token;
    },

    syncStatusEntry() {
      const store = useOpenFinanceStore();
      return store.syncStatus.data[this.account.id] ?? null;
    },

    formattedLastSync() {
      const ts = this.syncStatusEntry?.balance_updated_at;
      if (ts) {
        return new Date(ts).toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      if (this.syncStatusEntry?.last_sync_requested_at) {
        return "Aguardando atualização...";
      }
      return "Nunca sincronizado";
    },

    syncButtonLabel() {
      if (this.isSyncing) return "Sincronizando…";
      if (this.cooldownRemaining > 0) return this.cooldownLabel;
      return "Atualizar";
    },

    cooldownLabel() {
      const total = this.cooldownRemaining;
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      if (h > 0) return `Disponível em ${h}h ${m}m`;
      return `Disponível em ${m}m`;
    },

    syncTooltip() {
      return "Ao atualizar, todas as contas desta instituição são sincronizadas juntas. A atualização manual está disponível 1× a cada 4 horas por instituição.";
    },
  },

  watch: {
    // Recalculate cooldown when the store entry changes (e.g. after triggerSync refreshes status)
    syncStatusEntry: {
      immediate: true,
      handler() {
        this.recalculateCooldown();
      },
    },
  },

  mounted() {
    this.recalculateCooldown();
    // Tick every 60s — cooldown is shown in hours/minutes
    this.cooldownTimer = setInterval(() => this.recalculateCooldown(), 60_000);
  },

  unmounted() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  },

  methods: {
    recalculateCooldown() {
      const store = useOpenFinanceStore();
      this.cooldownRemaining = store.cooldownSecondsFor(this.account.id);
    },

    async reloadData() {
      await useAppStore().getAccounts();
    },

    openModal(value) {
      this.$emit("editAccount", value);
    },

    formatValue(valor) {
      return useAppStore().formatValue(valor);
    },

    emitAccDetail() {
      this.$emit("accDetail", this.account);
    },

    async requestSync() {
      if (this.isSyncing || this.cooldownRemaining > 0) return;
      this.isSyncing = true;
      try {
        await useOpenFinanceStore().triggerSync(this.account.id);
        this.$toast.add({
          severity: "info",
          summary: "Sincronização iniciada",
          detail:
            "Todas as contas desta instituição serão atualizadas em instantes.",
          life: 4000,
        });
      } catch (error) {
        const errorCode = error.response?.data?.error;
        if (errorCode === "cooldown") {
          const retryAfter = error.response?.data?.retry_after ?? 0;
          if (retryAfter > 0) {
            this.cooldownRemaining = retryAfter;
          }
          this.$toast.add({
            severity: "info",
            summary: "Atualização recente",
            detail: `Esta instituição foi sincronizada recentemente. ${this.cooldownLabel}.`,
            life: 5000,
          });
        } else if (errorCode === "daily_limit") {
          this.$toast.add({
            severity: "warn",
            summary: "Limite diário atingido",
            detail:
              "Limite diário de atualizações atingido. Os dados são sincronizados automaticamente todos os dias.",
            life: 6000,
          });
        } else {
          this.$toast.add({
            severity: "error",
            summary: "Erro na sincronização",
            detail:
              "Não foi possível iniciar a sincronização. Tente novamente mais tarde.",
            life: 5000,
          });
        }
        // Refresh cooldown state from server even on error (cooldown may have been set)
        await useOpenFinanceStore().getSyncStatus();
      } finally {
        this.isSyncing = false;
        this.recalculateCooldown();
      }
    },

    confirmDelete(id) {
      const isOpenFinance = !!this.account.open_finance_account_id;
      const message = isOpenFinance
        ? "Tem certeza que deseja remover a conexão com esta instituição?\n\nTodas as contas desta instituição serão removidas, incluindo todas as transações e histórico importados. Esta ação não pode ser desfeita."
        : "Você tem certeza que deseja deletar?";

      this.confirm.require({
        message,
        header: "Deletar",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Deletar",
        rejectLabel: "Voltar",
        accept: () => {
          this.deleteAccount(id);
        },
      });
    },

    deleteAccount(id) {
      const isOpenFinance = !!this.account.open_finance_account_id;
      const endpoint = isOpenFinance
        ? `/open_finance/account/${id}`
        : `/bank_account/${id}`;

      this.isDeleting = true;
      axios
        .delete(endpoint, {
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .then(() => {
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Conta removida com sucesso!",
            life: 3000,
          });
          setTimeout(() => {
            this.reloadData();
          }, 1000);
        })
        .catch((error) => {
          if (error.response?.status == 400) {
            this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail: "Erro ao deletar. A conta possui histórico no sistema.",
              life: 5000,
            });
          } else {
            this.$toast.add({
              severity: "error",
              summary: "Erro",
              detail:
                "Houve um erro ao remover a conta. Por favor, tente novamente.",
              life: 5000,
            });
          }
        })
        .finally(() => {
          this.isDeleting = false;
        });
    },
  },

  setup() {
    return { confirm: useConfirm() };
  },
};
</script>

<style lang="scss" scoped>
i {
  cursor: pointer;
  user-select: none;
}

.account-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;

  h1 {
    font-size: 14px;
    font-weight: bold;
    margin: 0;
  }

  p {
    font-size: 12px;
    font-weight: 300;
    margin: 0;
  }
}

.account-header-title {
  display: flex;
  align-items: center;
  max-width: calc(100% - 60px);
}

.balance {
  display: flex;
  align-items: center;
  margin: 20px 0;
  justify-content: space-between;

  p {
    font-size: 14px;
    font-weight: 400;
    margin: 0;
  }
}

.sync-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.sync-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);

  strong {
    color: #fff;
  }
}

.btn-sync {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 6px;
  color: #fff;
  font-size: 11px;
  padding: 4px 10px;
  cursor: pointer;
  transition: border-color 0.2s, opacity 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: rgba(255, 255, 255, 0.8);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.details {
  text-align: center;

  p {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0px;
    color: #cb5540;
    margin: 0;
    cursor: pointer;
  }
}

.box-dark {
  margin: 1rem;
}

.p-confirm-dialog-message {
  white-space: pre-line;
}

.bank-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
}

.bank-details {
  margin-left: 10px;
}
</style>
